import { useState, useCallback } from 'react';
import { useTenant } from '../context/TenantContext.js';
import { subscribers as subscriberApi, plans as plansApi } from '../api/client.js';
import { useApi } from '../hooks/useApi.js';
import { Badge } from '../components/Badge.js';
import { Modal } from '../components/Modal.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { EmptyState } from '../components/EmptyState.js';
import { useToast } from '../components/Toast.js';
import type { Subscriber } from '../types/index.js';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
];

const PAGE_SIZE = 20;

export function UsersPage() {
  const { activeTenantId } = useTenant();
  const { toast } = useToast();
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [selectedUser, setSelectedUser] = useState<Subscriber | null>(null);
  const [actionLoading, setActionLoading] = useState('');
  const [cancelTarget, setCancelTarget] = useState<Subscriber | null>(null);

  const fetcher = useCallback(
    () =>
      activeTenantId
        ? subscriberApi.list(activeTenantId, {
            status: status || undefined,
            limit: PAGE_SIZE,
            offset,
          })
        : Promise.resolve({ subscribers: [] as Subscriber[], total: 0 }),
    [activeTenantId, status, offset],
  );

  const { data, loading, error, refetch } = useApi(fetcher, [activeTenantId, status, offset]);

  const subs = data?.subscribers ?? [];
  const total = data?.total ?? 0;

  const handleStatusChange = (val: string) => {
    setStatus(val);
    setOffset(0);
  };

  const handleCancelConfirm = async () => {
    if (!activeTenantId || !cancelTarget) return;
    setActionLoading(cancelTarget.discord_user_id);
    try {
      await subscriberApi.cancel(activeTenantId, cancelTarget.discord_user_id);
      toast('Subscription cancelled');
      await refetch();
      setSelectedUser(null);
    } catch (err) {
      toast('Failed to cancel subscription', 'error');
    } finally {
      setActionLoading('');
      setCancelTarget(null);
    }
  };

  const handleExtend = async (sub: Subscriber, days: number) => {
    if (!activeTenantId) return;
    setActionLoading(sub.discord_user_id);
    try {
      await subscriberApi.extend(activeTenantId, sub.discord_user_id, days);
      toast(`Extended by ${days} days`);
      await refetch();
      setSelectedUser(null);
    } catch (err) {
      toast('Failed to extend subscription', 'error');
    } finally {
      setActionLoading('');
    }
  };

  if (error) return <ErrorMessage message={error} onRetry={() => void refetch()} />;

  return (
    <>
      <div className="filter-row">
        {STATUS_OPTIONS.map((opt) => (
          <div
            key={opt.value}
            className={`filter-pill ${status === opt.value ? 'active' : ''}`}
            onClick={() => handleStatusChange(opt.value)}
          >
            {opt.label}
          </div>
        ))}
        <div className="filter-spacer" />
        <span style={{ fontSize: 12, color: 'var(--t3)' }}>
          {total} total subscriber{total !== 1 ? 's' : ''}
        </span>
      </div>

      {loading && <LoadingSpinner text="Loading subscribers..." />}

      {!loading && subs.length === 0 && (
        <EmptyState
          title="No subscribers yet"
          description="Subscribers will appear here once users purchase plans through Discord."
        />
      )}

      {!loading && subs.length > 0 && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Total Spend</th>
                  <th>Payments</th>
                  <th>Expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {subs.map((sub) => (
                  <tr key={sub.discord_user_id}>
                    <td>
                      <div className="user-cell">
                        {sub.discord_avatar ? (
                          <img
                            src={sub.discord_avatar}
                            alt=""
                            className="user-cell-avatar"
                            style={{ objectFit: 'cover' }}
                          />
                        ) : (
                          <div
                            className="user-cell-avatar"
                            style={{ background: avatarGradient(sub.discord_user_id) }}
                          >
                            {(sub.discord_username || sub.discord_user_id).slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span className="user-cell-name">
                            {sub.discord_username || 'Unknown User'}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: 'monospace' }}>
                            {sub.discord_user_id}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge status={sub.status} />
                    </td>
                    <td style={{ color: sub.plan_label ? 'var(--t1)' : 'var(--t3)' }}>
                      {sub.plan_label ?? '—'}
                    </td>
                    <td style={{ fontFamily: 'var(--fd)', fontWeight: 600 }}>
                      ${Number(sub.total_spend).toFixed(2)}
                    </td>
                    <td style={{ color: 'var(--t2)' }}>{sub.total_payments}</td>
                    <td style={{ color: 'var(--t3)', fontSize: 12 }}>
                      {sub.expires_at ? formatExpiry(sub.expires_at) : '—'}
                    </td>
                    <td>
                      <button
                        className="btn-sm btn-ghost"
                        style={{ fontSize: 11, padding: '4px 10px' }}
                        onClick={() => setSelectedUser(sub)}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* pagination */}
          {total > PAGE_SIZE && (
            <div className="pagination">
              <button
                className="btn-sm btn-ghost"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </button>
              <span style={{ fontSize: 12, color: 'var(--t3)' }}>
                {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <button
                className="btn-sm btn-ghost"
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedUser && activeTenantId && (
        <ManageUserModal
          sub={selectedUser}
          tenantId={activeTenantId}
          onClose={() => setSelectedUser(null)}
          onCancel={() => setCancelTarget(selectedUser)}
          onExtend={(days) => void handleExtend(selectedUser, days)}
          onPlanChanged={() => { void refetch(); setSelectedUser(null); }}
          actionLoading={actionLoading === selectedUser.discord_user_id}
        />
      )}

      <ConfirmDialog
        open={cancelTarget !== null}
        title="Cancel Subscription"
        message={`Cancel ${cancelTarget?.discord_username || cancelTarget?.discord_user_id}'s subscription? Their role will be removed immediately.`}
        confirmLabel="Cancel Subscription"
        confirmColor="var(--red)"
        onConfirm={() => void handleCancelConfirm()}
        onCancel={() => setCancelTarget(null)}
      />
    </>
  );
}

function ManageUserModal({
  sub,
  tenantId,
  onClose,
  onCancel,
  onExtend,
  onPlanChanged,
  actionLoading,
}: {
  sub: Subscriber;
  tenantId: string;
  onClose: () => void;
  onCancel: () => void;
  onExtend: (days: number) => void;
  onPlanChanged: () => void;
  actionLoading: boolean;
}) {
  const { toast } = useToast();
  const [extendDays, setExtendDays] = useState(30);
  const [changingPlan, setChangingPlan] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [planChangeLoading, setPlanChangeLoading] = useState(false);

  const plansFetcher = useCallback(
    () => plansApi.list(tenantId),
    [tenantId],
  );
  const { data: plansData } = useApi(plansFetcher, [tenantId]);
  const planList = plansData?.plans.filter((p) => p.active) ?? [];

  const handleChangePlan = async () => {
    if (!selectedPlanId) return;
    setPlanChangeLoading(true);
    try {
      await subscriberApi.changePlan(tenantId, sub.discord_user_id, selectedPlanId);
      toast('Plan changed');
      onPlanChanged();
    } catch (err) {
      toast('Failed to change plan', 'error');
    } finally {
      setPlanChangeLoading(false);
    }
  };

  const isActive = sub.status === 'active' || sub.status === 'renewing';

  return (
    <Modal open={true} onClose={onClose} title="Manage Subscription" width={440}>
      <div style={{ padding: '0 24px 24px' }}>
        {/* user info header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '14px 0', borderBottom: '1px solid var(--b1)' }}>
          {sub.discord_avatar ? (
            <img src={sub.discord_avatar} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: 10, background: avatarGradient(sub.discord_user_id), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'var(--t1)' }}>
              {(sub.discord_username || sub.discord_user_id).slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <div style={{ fontFamily: 'var(--fd)', fontSize: 16, fontWeight: 700 }}>
              {sub.discord_username || 'Unknown User'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--t3)', fontFamily: 'monospace' }}>
              {sub.discord_user_id}
            </div>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Badge status={sub.status} />
          </div>
        </div>

        {/* subscription details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20, fontSize: 13 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--t3)', marginBottom: 4 }}>Plan</div>
            <div style={{ color: 'var(--t1)', fontWeight: 500 }}>{sub.plan_label || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--t3)', marginBottom: 4 }}>Expires</div>
            <div style={{ color: 'var(--t1)' }}>{sub.expires_at ? formatExpiry(sub.expires_at) : '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--t3)', marginBottom: 4 }}>Total Spend</div>
            <div style={{ fontFamily: 'var(--fd)', fontWeight: 600 }}>${Number(sub.total_spend).toFixed(2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--t3)', marginBottom: 4 }}>Payments</div>
            <div style={{ color: 'var(--t2)' }}>{sub.total_payments}</div>
          </div>
        </div>

        {/* actions */}
        {isActive && (
          <>
            {/* extend */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Extend Subscription</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[7, 14, 30, 90].map((d) => (
                  <button
                    key={d}
                    className={`btn-sm ${extendDays === d ? 'btn-primary' : 'btn-ghost'}`}
                    style={{ fontSize: 11, padding: '5px 10px' }}
                    onClick={() => setExtendDays(d)}
                  >
                    {d}d
                  </button>
                ))}
                <button
                  className="btn-sm btn-primary"
                  style={{ fontSize: 11, padding: '5px 14px', marginLeft: 'auto' }}
                  disabled={actionLoading}
                  onClick={() => onExtend(extendDays)}
                >
                  {actionLoading ? '...' : `+ ${extendDays} days`}
                </button>
              </div>
            </div>

            {/* change plan */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Change Plan</div>
              {!changingPlan ? (
                <button
                  className="btn-sm btn-ghost"
                  style={{ fontSize: 12 }}
                  onClick={() => setChangingPlan(true)}
                >
                  Switch to a different plan...
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <select
                    className="form-input"
                    style={{ flex: 1, fontSize: 12 }}
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                  >
                    <option value="">Select a plan...</option>
                    {planList
                      .filter((p) => p.id !== sub.plan_id)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label} — ${Number(p.priceUsd).toFixed(2)}
                        </option>
                      ))}
                  </select>
                  <button
                    className="btn-sm btn-primary"
                    style={{ fontSize: 11, padding: '5px 12px' }}
                    disabled={!selectedPlanId || planChangeLoading}
                    onClick={() => void handleChangePlan()}
                  >
                    {planChangeLoading ? '...' : 'Change'}
                  </button>
                  <button
                    className="btn-sm btn-ghost"
                    style={{ fontSize: 11, padding: '5px 8px' }}
                    onClick={() => { setChangingPlan(false); setSelectedPlanId(''); }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* cancel */}
            <div style={{ borderTop: '1px solid var(--b1)', paddingTop: 16 }}>
              <button
                className="btn-sm btn-ghost"
                style={{ fontSize: 12, color: 'var(--red)', width: '100%', justifyContent: 'center' }}
                disabled={actionLoading}
                onClick={onCancel}
              >
                {actionLoading ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            </div>
          </>
        )}

        {!isActive && (
          <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 13, color: 'var(--t3)' }}>
            This subscription is {sub.status}. The user can resubscribe via <code>/menu</code> in Discord.
          </div>
        )}
      </div>
    </Modal>
  );
}

function formatExpiry(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return `Expired ${Math.abs(diffDays)}d ago`;
  if (diffDays === 0) return 'Expires today';
  if (diffDays <= 7) return `${diffDays}d left`;
  return d.toLocaleDateString();
}

function avatarGradient(id: string): string {
  const gradients = [
    'linear-gradient(135deg,#818cf8,#5eead4)',
    'linear-gradient(135deg,#f87171,#fbbf24)',
    'linear-gradient(135deg,#4ade80,#22d3ee)',
    'linear-gradient(135deg,#a78bfa,#f472b6)',
    'linear-gradient(135deg,#fbbf24,#f97316)',
    'linear-gradient(135deg,#22d3ee,#3b82f6)',
    'linear-gradient(135deg,#f472b6,#8b5cf6)',
    'linear-gradient(135deg,#34d399,#a78bfa)',
  ];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  return gradients[Math.abs(hash) % gradients.length]!;
}
